import { useInfiniteQuery } from '@tanstack/react-query';

import { getContestEntrants } from '../domains/services/contest.service.api';
import { STALE_TIME, INFINITE_SCROLL_PAGE_SIZE } from '../constants/query';
import type { UserEntrant } from '../domains/services/interfaces/entrant.interface';

type UseContestEntrantsInfiniteProps = {
  contestId?: string;
  pageSize?: number;
  enabled?: boolean;
  source?: 'contest_detail_entrants' | 'upcoming_entrants';
};

export const useContestEntrantsInfinite = ({
  contestId,
  pageSize = INFINITE_SCROLL_PAGE_SIZE,
  enabled = true,
  source = 'contest_detail_entrants',
}: UseContestEntrantsInfiniteProps) => {
  const result = useInfiniteQuery({
    queryKey: ['contestEntrantsInfinite', contestId, source],
    queryFn: ({ pageParam = 0 }) =>
      getContestEntrants({
        contestId,
        limit: pageSize,
        offset: pageParam,
      }),
    enabled: enabled && !!contestId,
    staleTime: STALE_TIME,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.offset + pageSize;
      if (nextPage >= lastPage.total) {
        return undefined;
      }
      return nextPage;
    },
  });

  const entrants: UserEntrant[] = result.data?.pages.flatMap((page) => page.data) || [];

  const calculatedHasNextPage = result.data?.pages[0]?.total
    ? entrants.length < result.data.pages[0].total
    : false;

  return {
    ...result,
    entrants,
    itemsTotal: result.data?.pages[0]?.total || 0,
    hasNextPage: calculatedHasNextPage,
  };
};
